import React from "react";
import { Box, TextField, InputAdornment, Typography } from "@mui/material";
import { formFieldStyles } from "../../../styles/formFieldStyles";
import { colors, floatingLabelMaskSx } from "../../../styles/theme";
import InfoTooltip from "../overlays/InfoTooltip";

interface InputFieldProps {
  label: string;
  value: string;
  onChange?: (value: string) => void;
  /** Beim Verlassen des Feldes – für Blur-Speichern (siehe docs/adr/004_autosave_dirty_check.md) */
  onBlur?: (value: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  fullWidth?: boolean;
  required?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  labelBackgroundColor?: string;
  error?: string;
  /** Erklärt das Feld bei Hover übers Label: was es ist und welche Auswirkung es hat */
  tooltip?: React.ReactNode;
  /** MUI-Größe des TextFields (Standard: 'small') */
  size?: "small" | "medium";
  /** Setzt den Fokus beim Einhängen – für Felder, die erst auf eine Aktion hin erscheinen */
  autoFocus?: boolean;
  sx?: any;
}

/**
 * Borderless Design mit leicht getöntem Hintergrund
 */
/**
 * Komponente für Text-Eingaben
 * Für numerische Eingaben mit Formatierung verwende InputFieldNumber
 */
const InputField: React.FC<InputFieldProps> = ({
  label,
  value,
  onChange,
  onBlur,
  placeholder,
  readOnly = false,
  fullWidth = true,
  required = false,
  startIcon,
  endIcon,
  labelBackgroundColor = colors.background.default,
  error,
  tooltip,
  size = "small",
  autoFocus = false,
  sx = {},
}) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange && !readOnly) {
      onChange(event.target.value);
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (onBlur && !readOnly) {
      onBlur(event.target.value);
    }
  };

  const labelNode = tooltip ? (
    <InfoTooltip title={tooltip} placement="top">
      <span>{label}</span>
    </InfoTooltip>
  ) : (
    label
  );

  return (
    // formFieldStyles.container bringt ein mb, das nur für den alten Flex-/Stack-Formularbau
    // gedacht war – in einem SubSection-Raster (columns) übernimmt bereits dessen rowGap den
    // Zeilenabstand einheitlich; ein zusätzliches Feld-mb würde nur diese eine Zeile größer
    // machen als die übrigen (wie bei InputFieldNumber schon abgestellt).
    <Box sx={{ ...formFieldStyles.container, mb: 0, ...sx }}>
      <TextField
        fullWidth={fullWidth}
        variant="outlined"
        label={labelNode}
        required={required}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        type="text"
        error={!!error}
        size={size}
        autoFocus={autoFocus}
        slotProps={{
          input: {
            readOnly: readOnly,
            startAdornment: startIcon ? (
              <InputAdornment position="start">{startIcon}</InputAdornment>
            ) : undefined,
            endAdornment: endIcon ? (
              <InputAdornment position="end">{endIcon}</InputAdornment>
            ) : undefined,
          },
        }}
        sx={{
          // Pill-Radius, Schatten und Hover-Verstärkung kommen bereits aus dem Theme
          // (MuiOutlinedInput). Hier bleibt nur, was vom Error-State abhängt und das Theme
          // deshalb nicht pauschal setzen kann.
          "& .MuiOutlinedInput-root": {
            "&:hover fieldset": {
              borderColor: error
                ? colors.status.error.main
                : colors.border.focus,
            },
            "&.Mui-focused fieldset": {
              borderColor: error
                ? colors.status.error.main
                : colors.border.focus,
              borderWidth: 2,
            },
            "&.Mui-focused": {
              boxShadow: `0 4px 12px ${error ? colors.shadow.error : colors.shadow.focus}`,
            },
          },
          // Label-Farbe/-Gewicht (gefüllt/leer/Pflichtfeld/Fokus) kommt aus dem Theme
          // (MuiInputLabel) – hier nur die Hintergrundmaskierung hinter dem hochgewanderten Label.
          ...floatingLabelMaskSx(labelBackgroundColor),
        }}
      />
      {error && (
        <Typography
          variant="caption"
          color="error"
          sx={{
            mt: 1,
            display: "block",
            fontSize: "0.75rem",
            fontWeight: 400,
            textAlign: "right",
          }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default InputField;
